/**
 * Store module for management of user permissions.
 * It provides info about viewing permissiong of the current user.
 */


/**
 * Names of the apps we have to query for permissions.
 */
const APP_NAMES = {
    trackLayer: 'TrackWMS',
    panoramaLayer: 'PanoramaWMS',
    panoramas: 'MomaData',
};

/**
 * All of the listed capabilities need to be present to grant
 * a respective permission to the user.
 */
const REQUIRED_CAPABILITIES = {
    panoramas: [
        'GetTiling',
        'GetPanoramaLocation',
        'GetPanorama',
    ],
    panoramaLayer: [
        'GetMap',
        'GetFeatureInfo',
        'GetCapabilities',
    ],
    trackLayer: [
        'GetMap',
        'GetFeatureInfo',
        'GetCapabilities',
    ],
};

/**
 * Utility function for comparing arrays of capabilities.
 * @param {*[]} a
 * @param {*[]} b
 * @returns {boolean}
 */
const isSupersetOf = (a, b) => (
    b.every(v => a.includes(v))
);

const DEFAULT_PERMISSIONS = {
    trackLayer: false,
    panoramaLayer: false,
    panoramas: false,
};

/**
 * Names of mutations further used by their variable names, not values.
 * E.g. first mutation is referred by `[SET_SPLITPANES_SIZE]`,
 * not `'setSplitpanesSize'`.
 */
const mutationNames = {
    SET_PERMISSIONS: 'setPermissions',
};

const mutations = {
    /**
     * Viewing permissions setter mutation.
     * @param {Object} permissions
     * @param {boolean} permissions.trackLayer perm. to view map track layer
     * @param {boolean} permissions.panoramaLayer perm. to view map panorama layer
     * @param {boolean} permissions.panoramas perm. to view panoramas
     */
    [mutationNames.SET_PERMISSIONS]: (state, permissions) => {
        state.permissions = permissions;
    },
};

const state = {
    /**
     * User viewing permissions.
     * @param {Object} permissions
     * @param {boolean} permissions.trackLayer perm. to view map track layer
     * @param {boolean} permissions.panoramaLayer perm. to view map panorama layer
     * @param {boolean} permissions.panoramas perm. to view panoramas
     */
    permissions: { ...DEFAULT_PERMISSIONS },
};

const getters = {
    /**
     * Permission to view map track layer
     * @type {boolean}
     */
    trackLayerPermission: ({ permissions: { trackLayer } }) => trackLayer,

    /**
     * Permission to view map panorama layer
     * @type {boolean}
     */
    panoramaLayerPermission: ({ permissions: { panoramaLayer } }) => (
        panoramaLayer
    ),

    /**
     * Permission to view panoramas
     * @type {boolean}
     */
    panoramasPermission: ({ permissions: { panoramas } }) => panoramas,
};

const actions = {
    /**
     * Fetches user permissions from api.
     */
    fetchPermissions: async ({ dispatch, rootGetters: { momaURL } }) => {
        const url = momaURL('capabilities');
        const data = {
            apps: Object.values(APP_NAMES),
        };

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });

            const { userCapabilities } = await response.json();
            const permissions = {};

            Object.entries(APP_NAMES).forEach(([perm, name]) => {
                const app = userCapabilities.find(
                    ({ appName }) => appName === name,
                ) || {};
                const { capabilities = [] } = app;

                permissions[perm] = isSupersetOf(
                    capabilities,
                    REQUIRED_CAPABILITIES[perm],
                );
            });

            dispatch('setPermissions', permissions);
        } catch {
            dispatch('setPermissions', DEFAULT_PERMISSIONS);
        }
    },

    /**
     * Sets viewing permissions.
     * @param {Object} permissions
     * @param {boolean} permissions.trackLayer perm. to view map track layer
     * @param {boolean} permissions.panoramaLayer perm. to view map panorama layer
     * @param {boolean} permissions.panoramas perm. to view panoramas
     */
    setPermissions: ({ commit }, permissions) => {
        commit(mutationNames.SET_PERMISSIONS, permissions);
    },

    /**
     * Sets track layer viewing permission.
     * @param {boolean} permission
     */
    setTrackLayerPermission: (
        { dispatch, state: { permissions } },
        permission,
    ) => {
        dispatch('setPermissions', { ...permissions, trackLayer: permission });
    },

    /**
     * Sets panorama layer viewing permission.
     * @param {boolean} permission
     */
    setPanoramaLayerPermission: (
        { dispatch, state: { permissions } },
        permission,
    ) => {
        dispatch('setPermissions', {
            ...permissions,
            panoramaLayer: permission,
        });
    },

    /**
     * Sets panorama viewing permission.
     * @param {boolean} permission
     */
    setPanoramasPermission: (
        { dispatch, state: { permissions } },
        permission,
    ) => {
        dispatch('setPermissions', { ...permissions, panoramas: permission });
    },
};

/**
 * Vuex store module object, created by joining
 * actions, getters,  and state objects.
 */
export default {
    namespaced: true,
    actions,
    getters,
    mutationNames,
    mutations,
    state,
};
